import React, { useState } from 'react';

// A genuinely interactive machete anatomy diagram. Hover (desktop) or tap
// (mobile) any hotspot — or its list row — to highlight that region of the
// blade and read what it does. Educates buyers on build quality (full tang,
// fullers, drop-forged edge) on the Specifications page.

interface Part {
  id: string;
  n: number;
  name: string;
  tagline: string;
  body: string;
  marker: { x: number; y: number };
}

const PARTS: Part[] = [
  {
    id: 'tip', n: 1, name: 'Tip', tagline: 'Precision point',
    body: 'The forward point. Used for piercing, detail work, and controlled scoring where the full belly would be too much blade.',
    marker: { x: 948, y: 150 },
  },
  {
    id: 'belly', n: 2, name: 'Belly · sweet spot', tagline: 'Where the force lands',
    body: 'The curved forward section that carries the most cutting force. This is the sweet spot where a chopping stroke should land.',
    marker: { x: 700, y: 252 },
  },
  {
    id: 'edge', n: 3, name: 'Cutting edge', tagline: 'Drop-forged SAE 1075',
    body: 'The primary cutting edge, drop-forged from SAE 1075 high-carbon steel. It takes a keen edge and comes back to shave-sharp with a file in the field.',
    marker: { x: 450, y: 232 },
  },
  {
    id: 'fullers', n: 4, name: 'Fullers', tagline: 'Three-groove signature',
    body: 'Three longitudinal grooves, the Tres Canales signature. They lighten the blade and add rigidity without sacrificing strength.',
    marker: { x: 560, y: 150 },
  },
  {
    id: 'spine', n: 5, name: 'Spine', tagline: 'Mass & control',
    body: 'The thick, unsharpened back of the blade. It carries the mass that drives every chop and gives you a safe place to guide or baton the blade.',
    marker: { x: 632, y: 108 },
  },
  {
    id: 'tang', n: 6, name: 'Full tang', tagline: 'Runs handle-length',
    body: 'The steel runs the full length of the handle, so nothing is glued or pinned on. Full-tang construction is the single biggest reason these blades survive decades of real work.',
    marker: { x: 250, y: 150 },
  },
  {
    id: 'handle', n: 7, name: 'Handle & lanyard', tagline: 'Riveted, all-weather',
    body: 'Riveted hardwood or all-weather polymer scales, with a lanyard hole to secure a wrist strap for safe, controlled swings.',
    marker: { x: 112, y: 214 },
  },
];

const MacheteAnatomy: React.FC = () => {
  const [active, setActive] = useState<string>('tang');
  const current = PARTS.find((p) => p.id === active) ?? PARTS[0];
  const on = (id: string) => active === id;

  // Shared classes for the ember highlight overlays (visible only when active).
  const hi = (id: string) =>
    `text-ember transition-opacity duration-300 ease-editorial motion-reduce:transition-none ${
      on(id) ? 'opacity-100' : 'opacity-0'
    }`;

  return (
    <figure className="bg-paper-bright border border-ink-200">

      {/* Diagram — full width so the blade reads large */}
      <div className="relative border-b border-ink-200 px-4 sm:px-8 py-8 lg:py-10 bg-[radial-gradient(circle_at_50%_45%,_#FDFBF4_0%,_#F1ECDE_100%)]">
        <figcaption className="absolute top-5 left-5 z-10 inline-flex items-center gap-2 bg-paper-bright/90 backdrop-blur-sm border border-ink-200 px-3 py-1.5">
          <span className="w-1.5 h-1.5 bg-ember rounded-full" aria-hidden="true" />
          <span className="font-mono text-[10px] tracking-micro uppercase text-ink-900">
            Interactive model
          </span>
        </figcaption>
        <p className="absolute top-5 right-5 z-10 hidden sm:block font-mono text-[10px] tracking-micro uppercase text-ink-400">
          Hover or tap a point
        </p>

        <svg
          viewBox="0 78 1000 208"
          className="w-full h-auto max-w-5xl mx-auto block"
          role="img"
          aria-label={`Machete anatomy: ${current.name}. ${current.body}`}
        >
            <defs>
              <linearGradient id="mm-steel" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#E7E9ED" />
                <stop offset="0.5" stopColor="#D2D6DC" />
                <stop offset="1" stopColor="#B9BEC5" />
              </linearGradient>
              <linearGradient id="mm-wood" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#DcB683" />
                <stop offset="1" stopColor="#B78A54" />
              </linearGradient>
            </defs>

            {/* Handle */}
            <rect x="48" y="138" width="212" height="74" rx="14" fill="url(#mm-wood)" stroke="#9A7038" strokeWidth="1.5" />
            {/* subtle full-tang strip through the handle */}
            <line x1="70" y1="175" x2="256" y2="175" stroke="#A9836A" strokeWidth="2" opacity="0.5" />
            {/* rivets */}
            <circle cx="150" cy="175" r="6" fill="#7C6A52" />
            <circle cx="196" cy="175" r="6" fill="#7C6A52" />
            {/* lanyard hole */}
            <circle cx="80" cy="175" r="9" fill="#8A6C3F" />
            <circle cx="80" cy="175" r="9" fill="none" stroke="#6E5430" strokeWidth="1.5" />

            {/* Bolster */}
            <rect x="252" y="132" width="24" height="86" rx="3" fill="#B4B9C0" stroke="#8E949B" strokeWidth="1" />

            {/* Blade */}
            <path
              d="M276,140 C430,128 600,118 720,116 C812,114 882,121 926,141 C947,151 958,160 961,173 C962,181 957,187 949,191 C860,233 720,245 560,237 C460,231 360,221 276,206 Z"
              fill="url(#mm-steel)"
              stroke="#9AA0A6"
              strokeWidth="1.5"
            />
            {/* Fullers (three grooves) */}
            <g stroke="#A2A8AF" strokeWidth="2" fill="none" strokeLinecap="round">
              <path d="M332,156 C500,148 640,145 772,148" />
              <path d="M332,163 C500,155 640,152 772,155" />
              <path d="M332,170 C500,162 640,159 772,162" />
            </g>

            {/* ---- Highlight overlays (ember, active only) ---- */}
            {/* Spine */}
            <path className={hi('spine')} d="M282,141 C450,129 610,119 720,117 C820,116 890,123 936,148"
              fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
            {/* Cutting edge */}
            <path className={hi('edge')} d="M282,205 C400,219 500,229 560,235 C720,246 852,235 950,191"
              fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
            {/* Belly */}
            <path className={hi('belly')} d="M600,240 C700,247 800,241 872,213"
              fill="none" stroke="currentColor" strokeWidth="7" strokeLinecap="round" />
            {/* Tip */}
            <circle className={hi('tip')} cx="948" cy="176" r="22" fill="none" stroke="currentColor" strokeWidth="4" />
            {/* Fullers */}
            <g className={hi('fullers')} stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round">
              <path d="M332,156 C500,148 640,145 772,148" />
              <path d="M332,163 C500,155 640,152 772,155" />
              <path d="M332,170 C500,162 640,159 772,162" />
            </g>
            {/* Full tang */}
            <line className={hi('tang')} x1="70" y1="175" x2="264" y2="175" stroke="currentColor" strokeWidth="4"
              strokeLinecap="round" strokeDasharray="2 9" />
            {/* Handle */}
            <g className={hi('handle')} fill="none" stroke="currentColor" strokeWidth="4">
              <rect x="48" y="138" width="212" height="74" rx="14" />
              <circle cx="80" cy="175" r="9" />
            </g>

            {/* ---- Hotspot markers ---- */}
            {PARTS.map((p) => (
              <g
                key={p.id}
                role="button"
                tabIndex={0}
                aria-label={p.name}
                aria-pressed={on(p.id)}
                className="cursor-pointer outline-none group"
                onMouseEnter={() => setActive(p.id)}
                onFocus={() => setActive(p.id)}
                onClick={() => setActive(p.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActive(p.id); }
                }}
              >
                {/* leader dot */}
                <circle
                  cx={p.marker.x} cy={p.marker.y} r={on(p.id) ? 16 : 13}
                  className={`transition-all duration-200 ease-editorial motion-reduce:transition-none group-focus-visible:stroke-ember ${
                    on(p.id) ? 'fill-ember stroke-ember' : 'fill-paper-bright stroke-ink-300'
                  }`}
                  strokeWidth="1.5"
                />
                <text
                  x={p.marker.x} y={p.marker.y + 4} textAnchor="middle"
                  className={`font-mono text-[13px] pointer-events-none select-none ${
                    on(p.id) ? 'fill-paper-warm' : 'fill-ink-700'
                  }`}
                >
                  {p.n}
                </text>
              </g>
            ))}
          </svg>
        </div>

      {/* Controls: parts list + active description */}
      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div className="p-6 sm:p-8 lg:p-10 lg:border-r border-ink-200">
          <p className="font-mono text-[10px] tracking-micro uppercase text-ember mb-5">
            — Anatomy of the blade
          </p>
          <ul className="space-y-1">
            {PARTS.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onMouseEnter={() => setActive(p.id)}
                  onFocus={() => setActive(p.id)}
                  onClick={() => setActive(p.id)}
                  aria-pressed={on(p.id)}
                  className={`w-full text-left flex items-center gap-3 px-3 py-2.5 border-l-2 transition-colors duration-150 ease-editorial motion-reduce:transition-none ${
                    on(p.id)
                      ? 'border-ember bg-ember/5'
                      : 'border-transparent hover:bg-paper hover:border-ink-200'
                  }`}
                >
                  <span
                    className={`w-6 h-6 flex-shrink-0 flex items-center justify-center font-mono text-[11px] rounded-full ${
                      on(p.id) ? 'bg-ember text-paper-warm' : 'bg-ink-100 text-ink-700'
                    }`}
                  >
                    {p.n}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className={`block text-sm ${on(p.id) ? 'text-ink-950 font-medium' : 'text-ink-900'}`}>
                      {p.name}
                    </span>
                    <span className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 truncate">
                      {p.tagline}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Active description */}
        <div className="p-6 sm:p-8 lg:p-10 flex items-center bg-paper/40">
          <div className="w-full">
            <p className="font-mono text-[10px] tracking-micro uppercase text-ember mb-3">
              Part {current.n} of {PARTS.length}
            </p>
            <h4 className="font-editorial italic text-3xl text-ink-950 tracking-tight mb-4">
              {current.name}.
            </h4>
            <p className="text-ink-700 leading-relaxed">{current.body}</p>
          </div>
        </div>
      </div>
    </figure>
  );
};

export default MacheteAnatomy;
